// hooks/useAuth.ts - Simplified authentication hook (Google + Email)
import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  resetPassword,
  signOut,
  onAuthStateChange,
  getUserProfile,
  UserProfile
} from '../services/authService';
import { deleteMyAccount } from '../services/accountDeletionService';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      setUser(user);
      
      if (user) {
        // Fetch user profile
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Sign in with Google
   */
  const handleSignInGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const profile = await signInWithGoogle();
      setUserProfile(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with Email/Password
   */
  const handleSignInEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const profile = await signInWithEmail(email, password);
      setUserProfile(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign up with Email/Password
   */
  const handleSignUpEmail = async (email: string, password: string, name: string) => {
    try {
      setError(null);
      setLoading(true);
      const profile = await signUpWithEmail(email, password, name);
      setUserProfile(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send password reset email
   */
  const handleResetPassword = async (email: string) => {
    try {
      setError(null);
      await resetPassword(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
      throw err;
    }
  };

  /**
   * Sign out
   */
  const handleSignOut = async () => {
    try {
      setError(null);
      await signOut();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign out');
      throw err;
    }
  };

  /**
   * Permanently delete the signed-in user's account and all their data
   * (GDPR right to erasure). Cannot be undone.
   */
  const handleDeleteAccount = async () => {
    try {
      setError(null);
      await deleteMyAccount();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      throw err;
    }
  };

  /**
   * Refresh user profile
   */
  const refreshProfile = async () => {
    if (user) {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    }
  };

  return {
    user,
    userProfile,
    loading,
    error,
    handleSignInGoogle,
    handleSignInEmail,
    handleSignUpEmail,
    handleResetPassword,
    handleSignOut,
    handleDeleteAccount,
    refreshProfile,
    isAuthenticated: !!user
  };
};